/**
 * The **Provider Configuration** dialog (ADR-040 §D-1 / §D-10; storyboard Flow V;
 * REQ-UI-156..-162).
 *
 * The single place an operator manages the external systems that **feed** this
 * deployment: add and remove providers, give each the typed interfaces that
 * carry data from it, configure those interfaces' connections, define what each
 * may accept, install trust material, and start or stop at any of the three
 * levels.
 *
 * # A collection control as much as a configuration screen
 *
 * This is where mission data *enters* Meridian, and four properties follow:
 *
 *   - **the filter's fail-safe default is ALLOW-ALL** — the exact inverse of the
 *     egress twin. An interface with no filter receives everything, because
 *     dropping sensor data at the perimeter is unrecoverable: the record exists
 *     nowhere else and no later act reconstructs it;
 *   - **start/stop is a kill switch at every level**, and on ingress "stop"
 *     means *stop collecting from this source*, with no queue behind it;
 *   - **stopping is non-destructive** (REQ-PROVIDER-010);
 *   - **every change is an audited event naming the full
 *     ``provider / interface / connection`` path** (REQ-PROVIDER-016), which is
 *     why the mutation routes nest.
 *
 * # Master–detail, not a wizard
 *
 * The roster lists **providers**. Selecting one opens a single scrollable pane
 * with sticky section headers: the provider's own fields, then its interfaces,
 * each expanding in place into its editor and that editor's connections. No
 * sub-tabs (ADR-024 §4).
 *
 * That this is not a wizard is the visible half of ADR-040 ruling R-2. Flow G's
 * *Onboard New Aggregator* **was** a wizard, because it provisioned one
 * aggregator once. This surface is opened as often to *stop* a provider as to
 * create one, and a wizard would put four clicks between an operator and a kill
 * switch that now has real effect.
 *
 * # RBAC (REQ-UI-165 / REQ-UI-143)
 *
 * Every authenticated role opens this surface and reads it, field-limited by the
 * BFF. For non-privileged roles the mutation controls are **absent, not
 * disabled** — a disabled Start button tells an operator the system has a
 * capability they may not use, which on this surface is an invitation to go
 * looking for a way to use it. The BFF enforces regardless; this hiding is
 * cosmetic (ADR-009 defence-in-depth), and the client never re-derives the field
 * split from its session roles.
 *
 * That rule governs *permissions only*. A control the operator is entitled to
 * but which the surface cannot perform stays visible and disabled with an
 * explanation — which on this surface is exactly one thing: the import dialog's
 * Install, blocked by an unresolved placeholder.
 *
 * # Nothing is fetched until it is opened (REQ-UI-156)
 *
 * The roster query is gated on `open`, and Radix unmounts this body on close.
 * Provider configuration changes only when an operator changes it, so fetching
 * it on mount would put a request on every session for a surface most never
 * open — it is configuration, not part of the roll-up.
 *
 * Cross-references:
 *   - ADR-040 §D-1 / §D-3 / §D-10, ruling R-2; ADR-024 §4; ADR-009.
 *   - REQ-UI-156..-162, -165, -143; REQ-PROVIDER-004 / -007 / -010 / -016.
 *   - Flow V (this surface); Flow L §1 (the entry points + role gating);
 *     Flow G (superseded).
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useMemo, useState } from "react";
import { useSession } from "@/auth/session-context";
import { DescriptionTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  CREATABLE_PROVIDER_INTERFACE_TYPES,
  type InterfaceState,
} from "@/providers/provider-catalog";
import { ProviderEditor } from "@/providers/provider-editor";
import { ProviderImportDialog } from "@/providers/provider-import-dialog";
import { ProviderInterfacesSection } from "@/providers/provider-interfaces-section";
import { ProviderRoster } from "@/providers/provider-roster";
import {
  isPrivilegedProviderView,
  isProjectedProvider,
  newProviderConfig,
  newProviderInterfaceConfig,
  toProviderConfig,
  type ProviderConfig,
  type ProviderInterfaceConfig,
} from "@/providers/provider-model";
import { PROVIDER_MUTATOR_ROLES, useProviderInterfaces } from "@/providers/use-provider-interfaces";
import {
  useDeleteProvider,
  useDeleteProviderConnection,
  useDeleteProviderInterface,
  useExportProviderBundle,
  useProviderInterfacesFor,
  useProviders,
  useSaveProvider,
  useSaveProviderInterface,
  useSetProviderConnectionState,
  useSetProviderInterfaceState,
  useSetProviderState,
} from "@/providers/use-providers";

/**
 * What the Providers dialog is for, disclosed from its title.
 *
 * Was a header subtitle. Kept as text an operator can reach rather than
 * deleted: "provider" is Meridian's own term for an external system that
 * feeds the deployment (ADR-040 generalized it over the native, aggregator
 * and CDS-receive kinds), so the first operator to open this needs it and
 * every one after does not.
 */
const PROVIDER_DIALOG_HELP =
  "The external systems that feed this deployment. Each provider owns one or more " +
  "interfaces, and each interface one or more connections.";

/** Props for {@link ProviderManagementDialog}. */
export interface ProviderManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Pre-select a provider by name, or by the name of an interface it owns — the
   * entity the operator came from on the System display.
   *
   * **Either level, resolved provider-first.** Rather than split this into two
   * props — which would let a caller pass the right name on the wrong one and
   * land silently on the first provider — one name is matched against providers
   * first and against interfaces second. Provider names are unique
   * deployment-wide and interface names are unique within a provider, so the
   * only ambiguity is a provider and an interface sharing a name, where the
   * provider wins: that is the level this dialog's roster is keyed by.
   *
   * When nothing matches (a name from a newer build, or an entity just deleted)
   * the dialog falls back to the first provider rather than rendering an empty
   * detail.
   */
  initialSelectionName?: string | null;
  /** Mints ids for new providers / interfaces / connections; injectable for tests. */
  makeId?: () => string;
}

/** Render the provider configuration dialog. */
export function ProviderManagementDialog({
  open,
  onOpenChange,
  initialSelectionName = null,
  makeId = () => crypto.randomUUID(),
}: ProviderManagementDialogProps): React.ReactElement {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          data-testid="provider-management-dialog"
          aria-describedby={undefined}
          // Focus the dialog container on open, not its first tabbable child.
          //
          // Radix's default is "focus the first tabbable element", and as of
          // 2026-10-10 that is the title's help trigger — a Radix Tooltip
          // trigger, which opens on focus. Left to the default, the
          // explanatory tooltip would pop open every single time an operator
          // opened this dialog, which is the opposite of the reason the prose
          // was moved into a tooltip in the first place. (Before that change
          // the default landed on the Close button, which is its own small
          // hazard: an accidental Enter on an opening dialog closed it.)
          //
          // Focusing the container is the conventional modal pattern — the
          // dialog is announced, Escape works, and Tab then walks the contents
          // in document order — and it leaves the tooltip's focus-to-open
          // behaviour intact for the keyboard user who actually tabs to it.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (event.currentTarget instanceof HTMLElement) {
              event.currentTarget.focus();
            }
          }}
          // `system-status-scope` is applied deliberately: this modal is the
          // System Status panel's ingress mutation surface and needs exactly
          // that panel's status tokens (the legible standby grey —
          // `--status-stale` is otherwise 1.38:1 on a light panel). Radix
          // portals to <body>, so the class has to travel with the content.
          className={cn(
            "system-status-scope fixed left-1/2 top-1/2 z-50 flex h-[80vh] w-[1080px] max-w-[95vw]",
            "-translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-md border",
            "border-border bg-bg-surface shadow-lg",
          )}
        >
          <DialogBody
            open={open}
            initialSelectionName={initialSelectionName}
            makeId={makeId}
            onClose={() => onOpenChange(false)}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The selection: an existing provider id, a pending create, or nothing. */
type Selection = { kind: "existing"; id: string } | { kind: "new" } | null;

/** The dialog's content — split out so it mounts only while open (REQ-UI-156). */
function DialogBody({
  open,
  initialSelectionName,
  makeId,
  onClose,
}: {
  open: boolean;
  initialSelectionName: string | null;
  makeId: () => string;
  /** Dismiss the whole surface — what the editor's Cancel does. */
  onClose: () => void;
}): React.ReactElement {
  const session = useSession();
  const mayMutate = session.hasAnyRole(PROVIDER_MUTATOR_ROLES);
  const providersQuery = useProviders(open);
  const providers = useMemo(() => providersQuery.data ?? [], [providersQuery.data]);

  const [requested, setRequested] = useState<Selection>(null);
  const [draftProviderId, setDraftProviderId] = useState<string | null>(null);
  // ONE expanded interface, never a set: two open editors are two independent
  // unsaved drafts of live ingest configuration.
  const [expandedInterfaceId, setExpandedInterfaceId] = useState<string | null>(null);
  const [interfaceDraft, setInterfaceDraft] = useState<ProviderInterfaceConfig | null>(null);

  const saveProvider = useSaveProvider();
  const deleteProvider = useDeleteProvider();
  const setProviderState = useSetProviderState();
  const saveInterface = useSaveProviderInterface();
  const deleteInterface = useDeleteProviderInterface();
  const setInterfaceState = useSetProviderInterfaceState();
  const setConnectionState = useSetProviderConnectionState();
  const deleteConnection = useDeleteProviderConnection();
  const exportProvider = useExportProviderBundle();
  // Import is roster-level, not provider-level: it CREATES a provider, so it has
  // no provider to belong to (REQ-UI-157).
  const [importOpen, setImportOpen] = useState(false);

  /**
   * Resolve the handed-over name to a provider id — **providers first,
   * interfaces second** (see :prop:`initialSelectionName`).
   *
   * The provider match is free: the roster is already loaded above, so a
   * handover from the Provider Manager node's list costs no request at all. The
   * interface fallback is the one that costs, and it is issued only while the
   * dialog is open and only when the provider match missed.
   *
   * ``provider_id`` on the interface read is privileged (REQ-UI-165), so for an
   * `operator` session the fallback resolves to ``null`` and the dialog lands on
   * the first provider. Degraded, not broken: a non-privileged reader can still
   * find the provider in the roster — and the provider-name path above needs no
   * privileged field at all, so the common case is not degraded for them.
   */
  const flatInterfaces = useProviderInterfaces(open && initialSelectionName !== null);
  const initialProviderId = useMemo(() => {
    if (initialSelectionName === null) return null;
    const providerMatch = providers.find((provider) => provider.name === initialSelectionName);
    if (providerMatch !== undefined) return providerMatch.provider_id;
    const match = (flatInterfaces.data ?? []).find((i) => i.name === initialSelectionName);
    return match?.provider_id ?? null;
  }, [initialSelectionName, providers, flatInterfaces.data]);

  /**
   * The selection actually rendered — **derived, not stored** (coding-standards
   * §2.5: prefer derived state over an effect).
   *
   * ``requested`` is only what the operator last clicked. Resolving it against
   * the live roster on every render is what makes the pane self-heal in the
   * three cases that otherwise strand it on an empty detail: the roster has not
   * arrived yet, the selected provider was just deleted, and the dialog was
   * closed and reopened — which unmounts Radix's content and resets
   * ``requested`` while the query cache still holds the list. A stored selection
   * makes "nothing selected" and "the thing you selected is gone" render
   * identically, which on this surface reads as a provider that has vanished.
   */
  const selection: Selection = useMemo(() => {
    if (requested?.kind === "new") return requested;
    if (requested?.kind === "existing" && providers.some((p) => p.provider_id === requested.id)) {
      return requested;
    }
    const landing =
      (initialProviderId !== null
        ? providers.find((p) => p.provider_id === initialProviderId)
        : undefined) ?? providers[0];
    return landing ? { kind: "existing", id: landing.provider_id } : null;
  }, [requested, providers, initialProviderId]);

  const selectedProvider =
    selection?.kind === "existing"
      ? (providers.find((p) => p.provider_id === selection.id) ?? null)
      : null;

  const interfacesQuery = useProviderInterfacesFor(selectedProvider?.provider_id ?? null);
  const interfaces = useMemo(() => interfacesQuery.data ?? [], [interfacesQuery.data]);

  const selectProvider = (providerId: string): void => {
    setRequested({ kind: "existing", id: providerId });
    // A different provider's interfaces are a different set; carrying an
    // expansion or a half-typed draft across would attach it to the wrong parent.
    setExpandedInterfaceId(null);
    setInterfaceDraft(null);
  };

  const startProviderCreate = (): void => {
    setDraftProviderId(makeId());
    setRequested({ kind: "new" });
    setExpandedInterfaceId(null);
    setInterfaceDraft(null);
  };

  const providerDraft: ProviderConfig | null = useMemo(() => {
    if (selection?.kind === "new" && draftProviderId !== null) {
      return newProviderConfig(draftProviderId);
    }
    if (selectedProvider !== null && isPrivilegedProviderView(selectedProvider)) {
      return toProviderConfig(selectedProvider);
    }
    return null;
  }, [selection, draftProviderId, selectedProvider]);

  return (
    <>
      <header className="flex items-center gap-3 border-b border-border px-4 py-2">
        {/* Titled for the control that opens it, not for the subsystem behind
            it: the operator clicked "Providers…" and must land somewhere that
            says so. */}
        <Dialog.Title className="text-body font-medium text-text-primary">Providers</Dialog.Title>
        {/* The subtitle moved into the title's tooltip on 2026-10-10 (architect
            direction, with the rest of this dialog's standing prose). It told a
            first-time operator what a provider is; it told every subsequent one
            nothing, while taking header width the roster and detail panes want
            on a 280px-plus-form layout. */}
        <DescriptionTooltip
          testId="provider-dialog"
          label="Providers"
          description={PROVIDER_DIALOG_HELP}
        />
        <Dialog.Close asChild>
          <button
            type="button"
            aria-label="Close providers"
            data-testid="provider-dialog-close"
            className="ml-auto rounded p-1 text-text-secondary hover:bg-bg-elevated"
          >
            ✕
          </button>
        </Dialog.Close>
      </header>

      {/* Desktop-first (operator workstations): side-by-side master-detail.
          Below `md` the two stack instead, because a 280px roster beside a
          1080px form in a 375px viewport clips the form off-screen with no way
          to reach it — "does not overflow the page" is not the same as "usable". */}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <ProviderRoster
          providers={providers}
          selectedId={selection?.kind === "existing" ? selection.id : null}
          onSelect={selectProvider}
          mayMutate={mayMutate}
          onCreate={startProviderCreate}
          onImport={() => setImportOpen(true)}
          creating={selection?.kind === "new"}
          isLoading={providersQuery.isLoading}
          error={providersQuery.isError ? providersQuery.error.message : null}
        />

        {/* ONE scrollable pane across all three levels: the section headers stick
            against this scroller, and no child owns one of its own (ADR-024 §4). */}
        <main className="min-h-0 flex-1 overflow-auto" data-testid="provider-detail">
          {selection === null ? (
            <p className="px-4 py-3 text-body-sm text-text-muted">
              Select a provider to see its configuration.
            </p>
          ) : (
            <>
              {mayMutate && providerDraft !== null && (
                <ProviderEditor
                  // Remount on selection change so the form re-seeds cleanly.
                  key={selection.kind === "new" ? `new-${draftProviderId}` : selection.id}
                  value={providerDraft}
                  isNew={selection.kind === "new"}
                  interfaceCount={interfacesQuery.isSuccess ? interfaces.length : null}
                  otherNames={providers
                    .filter((p) => p.provider_id !== providerDraft.provider_id)
                    .map((p) => p.name)}
                  savePending={saveProvider.isPending}
                  statePending={setProviderState.isPending}
                  saveError={
                    saveProvider.isError ? describeProviderError(saveProvider.error.status) : null
                  }
                  onSave={(config) =>
                    saveProvider.mutate(
                      { config },
                      {
                        onSuccess: () => setRequested({ kind: "existing", id: config.provider_id }),
                      },
                    )
                  }
                  onDelete={() =>
                    selection.kind === "existing" &&
                    deleteProvider.mutate(selection.id, { onSuccess: () => setRequested(null) })
                  }
                  onSetState={(state: InterfaceState) =>
                    selection.kind === "existing" &&
                    setProviderState.mutate({ providerId: selection.id, state })
                  }
                  onExport={
                    selection.kind === "existing" && selectedProvider !== null
                      ? () =>
                          exportProvider.mutate({
                            providerId: selectedProvider.provider_id,
                            providerName: selectedProvider.name,
                          })
                      : undefined
                  }
                  exportPending={exportProvider.isPending}
                  exportError={
                    exportProvider.isError
                      ? "Could not export this provider. Nothing left the deployment."
                      : null
                  }
                />
              )}
              {!mayMutate && selectedProvider !== null && (
                <ProviderReadOnlyHeader name={selectedProvider.name} />
              )}

              {selectedProvider !== null && (
                <ProviderInterfacesSection
                  provider={{ name: selectedProvider.name, state: selectedProvider.state }}
                  projected={isProjectedProvider(selectedProvider)}
                  interfaces={interfaces}
                  isLoading={interfacesQuery.isLoading}
                  error={interfacesQuery.isError ? interfacesQuery.error.message : null}
                  expandedId={expandedInterfaceId}
                  onExpand={setExpandedInterfaceId}
                  mayMutate={mayMutate}
                  onCreate={() =>
                    setInterfaceDraft(
                      newProviderInterfaceConfig(
                        makeId(),
                        CREATABLE_PROVIDER_INTERFACE_TYPES[0].typeId,
                      ),
                    )
                  }
                  draft={interfaceDraft}
                  makeId={makeId}
                  savePending={saveInterface.isPending}
                  statePending={setInterfaceState.isPending || setConnectionState.isPending}
                  saveError={
                    saveInterface.isError
                      ? describeInterfaceError(saveInterface.error.status, saveInterface.error.body)
                      : null
                  }
                  onSave={(config) =>
                    saveInterface.mutate(
                      { providerId: selectedProvider.provider_id, config },
                      {
                        onSuccess: () => {
                          setInterfaceDraft(null);
                          setExpandedInterfaceId(config.interface_id);
                        },
                      },
                    )
                  }
                  onDelete={(interfaceId) =>
                    deleteInterface.mutate(
                      { providerId: selectedProvider.provider_id, interfaceId },
                      { onSuccess: () => setExpandedInterfaceId(null) },
                    )
                  }
                  onCancelDraft={() => setInterfaceDraft(null)}
                  onSetInterfaceState={(interfaceId, state) =>
                    setInterfaceState.mutate({
                      providerId: selectedProvider.provider_id,
                      interfaceId,
                      state,
                    })
                  }
                  onSetConnectionState={(interfaceId, connectionId, state) =>
                    setConnectionState.mutate({
                      providerId: selectedProvider.provider_id,
                      interfaceId,
                      connectionId,
                      state,
                    })
                  }
                  onDeleteConnection={(interfaceId, connectionId) =>
                    deleteConnection.mutate({
                      providerId: selectedProvider.provider_id,
                      interfaceId,
                      connectionId,
                    })
                  }
                />
              )}

              <footer className="flex items-center justify-end border-t border-border px-4 py-2">
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="provider-close"
                  className="rounded border border-border px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
                >
                  Close
                </button>
              </footer>
            </>
          )}
        </main>
      </div>

      {/* Mounted only while open, like the dialog itself: parsing is local, but
          the component holds the operator's selected file and there is no reason
          for it to exist before they ask for it. */}
      {importOpen && (
        <ProviderImportDialog
          onClose={() => setImportOpen(false)}
          onInstalled={(providerName) => {
            setImportOpen(false);
            // Land on what was just installed. The roster invalidation has been
            // issued but not necessarily settled, so this resolves by NAME
            // through the same path `initialSelectionName` uses rather than by
            // an id this client has never seen.
            const installed = providers.find((provider) => provider.name === providerName);
            if (installed !== undefined) {
              setRequested({ kind: "existing", id: installed.provider_id });
            }
          }}
        />
      )}
    </>
  );
}

/**
 * The provider's identity for a session that may not edit it.
 *
 * Deliberately not a disabled copy of {@link ProviderEditor}: a greyed-out form
 * of fields the BFF never sent would be a shape suggesting there is something
 * behind it (REQ-UI-143). The provider's *name* is in the all-roles field-set
 * and is what labels the interfaces below, so it is stated plainly and nothing
 * else is.
 */
function ProviderReadOnlyHeader({ name }: { name: string }): React.ReactElement {
  return (
    <div className="border-b border-border px-4 py-3" data-testid="provider-read-only-header">
      <h3 className="text-body font-medium text-text-primary">{name}</h3>
    </div>
  );
}

/**
 * Map a provider save failure to operator-facing copy (Flow V §9).
 *
 * Generic by design (REQ-UI-037 — no internal-error leakage), but specific where
 * the operator can act: a 409 is either a name they can change or a deployment
 * that is full, which are different next steps.
 */
function describeProviderError(status: number): string {
  if (status === 409) {
    return "Another provider already uses that name, or this deployment is full.";
  }
  if (status === 422) return "The server rejected this provider. Check the fields above.";
  if (status === 403) return "This session may not change provider configuration.";
  return "Could not save the provider. The change was not applied.";
}

/**
 * The BFF's typed refusals carry a `message` written for this surface (REQ-UI-037:
 * no internals, the operator's next step named) — the published-listener-range
 * refusal (REQ-PROVIDER-057 / REQ-PEER-114) names the connection, the port, the
 * range and the `.env` key to widen. Shown verbatim where present; a 422 whose
 * body is pydantic's field list is not typed and falls through to the generic copy.
 */
function typedRefusalMessage(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail !== "object" || detail === null) return null;
  const message = (detail as { message?: unknown }).message;
  return typeof message === "string" && message !== "" ? message : null;
}

/** Map an interface save failure to operator-facing copy. See {@link describeProviderError}. */
function describeInterfaceError(status: number, body?: unknown): string {
  if (status === 422) {
    const typed = typedRefusalMessage(body);
    if (typed !== null) return `The deployment refused this configuration: ${typed}`;
  }
  if (status === 409) {
    // Two distinct causes reach 409 here and the operator's next step differs, so
    // the copy names both rather than guessing: a duplicate name is theirs to fix,
    // a re-home attempt means they are editing through the wrong provider.
    return "That name is already used within this provider, or the interface belongs to another provider.";
  }
  if (status === 400) return "This interface does not belong to the selected provider.";
  if (status === 422) {
    // Named specifically, because the surface is supposed to have made an
    // impermissible trust/transport pair unreachable — a 422 here means the
    // stored value diverged from the registry, which is a different act from a
    // field the operator mistyped (Flow V §9).
    return "The server rejected this configuration — this interface type may not permit that trust mode and transport security combination. Check the fields above.";
  }
  if (status === 403) return "This session may not change provider configuration.";
  return "Could not save the interface. The change was not applied.";
}
